import { ErrorCode } from '@mogul/api-client';
import { parseApiErrors } from '@mogul/shared';
import { useContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';

import { authApi } from '../../api';
import { useAuth } from '../../auth';
import { OkModal } from '../../components/common/modal/OkModal.component';
import { LoginComponent } from '../../components/login/Login.component';
import { VerifyEmailComponent } from '../../components/verify-email/VerifyEmail.component';
import { VerifyTwoFactorAuthComponent } from '../../components/verify-two-factor-auth/VerifyTwoFactorAuth.component';
import { ModalStateContext } from '../../contexts/app-state-context/modal-state.context';

export const Login = () => {
  const {
    mfaRequired,
    verificationRequired,
    unverifiedEmail,
    login,
    verifyMfa,
    verifyEmail,
    reset,
  } = useAuth();
  const { setModal } = useContext(ModalStateContext);

  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    if (token !== null) {
      void verifyEmail({ token }).then(() => {
        setModal(OkModal('Email verified. You can now log in.'));
        history.replace(location.pathname);
      });
    }
  }, [location.search]);

  if (verificationRequired && unverifiedEmail !== undefined) {
    return (
      <VerifyEmailComponent
        email={unverifiedEmail}
        onVerifyPress={(token: string) => {
          void verifyEmail({ token }).then(
            () => {
              setModal(OkModal('Email verified. You can now log in.'));
            },
            error => {
              for (const { code } of parseApiErrors(error)) {
                if (code === ErrorCode.AuthUnauthorized) {
                  setModal(OkModal('Invalid Verification Code'));
                  return;
                }
              }

              throw error;
            },
          );
        }}
        resendEmail={() => {
          setModal(OkModal('Email Resent.'));

          void authApi.createVerification({
            emailVerificationData: { email: unverifiedEmail },
          });
        }}
        onClickLogin={() => reset()}
      />
    );
  }

  if (mfaRequired) {
    return (
      <VerifyTwoFactorAuthComponent
        verifyTwoFactorCode={otpCode =>
          verifyMfa({ otpCode }).catch(error => {
            for (const { code, detail } of parseApiErrors(error)) {
              if (code === ErrorCode.AuthUnauthorized) {
                setModal(OkModal(detail ?? 'Invalid code'));
                return;
              }
            }
          })
        }
      />
    );
  }

  return (
    <LoginComponent
      onLoginPress={input =>
        login({ username: input.login, password: input.password }).catch(
          error => {
            for (const { code } of parseApiErrors(error)) {
              if (code === ErrorCode.AuthUnauthorized) {
                setModal(OkModal('Wrong username/password'));
                return;
              }
            }

            throw error;
          },
        )
      }
    />
  );
};
